import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  StyleSheet,
  Text as RNText,
  type LayoutChangeEvent,
} from "react-native";

import { useTheme } from "react-native-paper";
import Svg, { Circle, G } from "react-native-svg";

import {
  finishWinCelebration,
  registerWinCelebrant,
} from "../game/winCelebration";
import { useReducedMotion } from "../hooks/useReducedMotion";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import { gameWordsInGraph } from "../utils/gameStateValidation";
import type { GameReport } from "../utils/gameReportUtils";
import {
  startMeasure,
  endMeasure,
  PerformanceMarks,
  PerformanceMeasures,
  startFrameRateMonitoring,
} from "../utils/performanceMonitor";
import AriadneMark from "./board/AriadneMark";
import BoardEdges from "./board/BoardEdges";
import BoardIndicators from "./board/BoardIndicators";
import BoardLabels from "./board/BoardLabels";
import { layoutBoard } from "./board/boardLayout";
import { buildBoardModel } from "./board/boardModel";
import BoardNodes from "./board/BoardNodes";
import { BoardOptionNodes, BoardSpokes } from "./board/BoardOptions";
import { useOptionHover } from "./board/options";
import { boardLabelsFor } from "./board/optionLabels";
import { NODE_R } from "./board/constants";
import { edgeWidthScale } from "./board/edgeStyle";
import { graphExtent, type Size } from "./board/layout";
import { motionSnapshot, useBoardMotion } from "./board/useBoardMotion";
import { useBoardData, type DisplayOverride } from "./board/useBoardData";
import { useBoardViewport } from "./board/useBoardViewport";
import { useViewportGestures } from "./board/useViewportGestures";
import { contentBounds, viewIndicators } from "./board/viewportLayout";
import { composeCamera, isIdentity } from "./board/viewport";
import DevBoardOverlay from "./dev/DevBoardOverlay";
import DevHud from "./dev/DevHud";
import DevPanel from "./dev/DevPanel";

const EDGE_SCALE = edgeWidthScale(NODE_R);

interface GraphVisualizationProps {
  height?: number;
  gameReport?: GameReport;
  pathDisplayModeOverride?: DisplayOverride;
  /** The move handler the word buttons use; option nodes call it too. */
  onMove?: (word: string) => void;
  /** The tutorial's hinted word, ringed on its option node. */
  hintWord?: string | null;
}

/**
 * The game board (and, with a gameReport, the report mini-graph). Drawn in
 * screen pixels: the SVG viewBox is the measured size, so nodes, edges and
 * labels keep fixed sizes however close or far apart the words are.
 */
const GraphVisualization: React.FC<GraphVisualizationProps> = ({
  height,
  gameReport,
  pathDisplayModeOverride,
  onMove,
  hintWord = null,
}) => {
  const { colors, customColors } = useTheme() as ExtendedTheme;
  const hovered = useOptionHover((s) => s.hovered);
  const setHovered = useOptionHover((s) => s.setHovered);
  const { input, graphData, status, streak, live, selectWord } = useBoardData(
    gameReport,
    pathDisplayModeOverride,
  );
  const [size, setSize] = useState<Size | null>(null);
  const reduced = useReducedMotion();

  React.useEffect(() => {
    startFrameRateMonitoring();
  }, []);
  // The live board runs the win wave; the report opens when it finishes.
  React.useEffect(() => (live ? registerWinCelebrant() : undefined), [live]);

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height: h } = e.nativeEvent.layout;
    setSize((prev) =>
      prev && Math.abs(prev.w - width) < 1 && Math.abs(prev.h - h) < 1
        ? prev
        : { w: width, h },
    );
  }, []);

  const onSelectWord = (word: string) => {
    startMeasure(PerformanceMarks.WORD_SELECTION);
    if (live && status === "playing") (onMove ?? selectWord)(word);
    endMeasure(
      PerformanceMarks.WORD_SELECTION,
      PerformanceMarks.WORD_SELECTION,
      PerformanceMeasures.WORD_SELECTION_TIME,
    );
  };

  const model = useMemo(() => (input ? buildBoardModel(input) : null), [input]);
  // A move replaces the options; a hover on the old ones must not linger.
  const current = model?.current ?? null;
  React.useEffect(() => setHovered(null), [current, setHovered]);
  const layout = useMemo(() => {
    if (!model || !size || !graphData || model.nodes.length === 0) return null;
    startMeasure(PerformanceMarks.GRAPH_RENDER);
    const out = layoutBoard(model, size, graphExtent(graphData), {
      playing: live && status === "playing",
      path: input?.playerPath ?? [],
    });
    endMeasure(
      PerformanceMarks.GRAPH_RENDER,
      PerformanceMarks.GRAPH_RENDER,
      PerformanceMeasures.GRAPH_RENDER_TIME,
    );
    return out;
  }, [model, size, graphData, live, status, input]);

  // Her current word (spec §5): the mark sits on the last word of her thread
  // while the thread is shown.
  const herWord =
    input && input.display.ai && input.aiPath.length > 0
      ? input.aiPath[input.aiPath.length - 1]
      : null;
  const herNode =
    herWord && layout
      ? (layout.nodes.find((n) => n.id === herWord) ?? null)
      : null;

  const snapshot = useMemo(
    () =>
      input && model && layout
        ? motionSnapshot(input, model, layout, streak, EDGE_SCALE)
        : null,
    [input, model, layout, streak],
  );
  const frame = useBoardMotion(snapshot, live, reduced, finishWinCelebration);
  // The player's zoom/pan: kept until the next move, then back to the fit.
  const bounds = useMemo(
    () => (layout ? contentBounds(layout) : null),
    [layout],
  );
  const moveKey = input
    ? `${input.startWord}|${input.targetWord}|${input.playerPath.join(">")}`
    : "";
  const viewport = useBoardViewport(size, bounds, moveKey, reduced);
  const gestures = useViewportGestures(viewport, live && !!layout);

  const container = [styles.container, height ? { height } : null];

  if (!graphData || status === "loading") {
    return (
      <View style={container}>
        <RNText>Loading Graph...</RNText>
      </View>
    );
  }
  if (
    input &&
    !gameWordsInGraph([input.startWord, input.targetWord], graphData)
  ) {
    return (
      <View style={container}>
        <RNText>This game was played on a previous word graph.</RNText>
      </View>
    );
  }
  if (!input || !model || !layout || !size) {
    return <View style={container} onLayout={onLayout} />;
  }

  const pos = new Map(layout.nodes.map((n) => [n.id, n]));
  const { view } = viewport;
  const camera = composeCamera(view, frame.camera);
  const { flash } = frame;
  const upp = 1 / camera.k;
  const indicators = isIdentity(view)
    ? layout.indicators
    : viewIndicators(layout.nodes, view, size);
  const flashNode = flash ? pos.get(flash.node) : undefined;
  return (
    <View
      ref={gestures.ref as never}
      style={[container, live ? styles.interactive : null]}
      onLayout={onLayout}
      {...gestures.handlers}
      accessibilityLabel={
        streak >= 2 ? `${streak} Local Optimal moves in a row` : undefined
      }
      accessibilityLiveRegion="polite"
    >
      <Svg width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`}>
        {/* Camera: the player's view, glide and nudge animate this transform, never the viewBox. */}
        <G
          transform={`translate(${camera.dx},${camera.dy}) scale(${camera.k})`}
        >
          <BoardSpokes
            current={model.current ? pos.get(model.current) : undefined}
            options={layout.options}
            ghosts={frame.ghosts}
            scaleOf={frame.optionScale}
            unitsPerPx={upp}
          />
          <BoardEdges
            links={model.links}
            pos={pos}
            palette={input.palette}
            scale={EDGE_SCALE}
            won={model.won}
            motionOf={(l) => frame.edge(l.key)}
            unitsPerPx={upp}
          />
          <BoardOptionNodes
            options={layout.options}
            ghosts={frame.ghosts}
            scaleOf={frame.optionScale}
            hovered={hovered}
            hint={hintWord}
            hintColor={customColors.currentNode}
            unitsPerPx={upp}
            onPress={onSelectWord}
            onHover={setHovered}
          />
          <BoardNodes
            nodes={layout.nodes}
            scaleOf={frame.nodeScale}
            targetFill={frame.targetFill}
            fillDuration={reduced ? 0 : 120}
            breathe={!reduced}
            unitsPerPx={upp}
            onPress={onSelectWord}
          />
          {herNode && (
            <AriadneMark
              x={herNode.px}
              y={herNode.py}
              r={herNode.r}
              color={input.palette.ai}
              at={`${input.aiPath.length}:${herNode.id}`}
              reduced={reduced}
              unitsPerPx={upp}
            />
          )}
          {flashNode && flash && (
            <Circle
              cx={flashNode.px}
              cy={flashNode.py}
              r={flashNode.r * 1.1}
              fill="#FFFFFF"
              opacity={flash.opacity}
            />
          )}
          <BoardLabels
            labels={boardLabelsFor(layout, frame, hovered, colors.onSurface)}
            color={colors.onSurface}
          />
          {live && (
            <DevBoardOverlay
              layout={layout}
              graph={graphData}
              size={size}
              unitsPerPx={upp}
            />
          )}
        </G>
        <BoardIndicators
          indicators={indicators}
          colors={input.palette}
          textColor={colors.onSurface}
        />
      </Svg>
      {live && <DevHud />}
      {live && <DevPanel onMove={onSelectWord} />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "transparent",
  },
  // Web: the board owns touch gestures (pan/pinch) and never selects text.
  interactive: { touchAction: "none", userSelect: "none" } as object,
});

export default GraphVisualization;
